import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Plus, 
  Download,
  Search,
  Filter,
  Calendar,
  Building2,
  User,
  MapPin,
  FileText,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  Eye,
  X,
  Check,
  Loader2,
  ArrowUpDown,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'interested',  label: 'Interested',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'submitted',   label: 'Submitted',   color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'awarded',     label: 'Awarded',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'contracted',  label: 'Contracted',  color: 'bg-purple-50 text-purple-700 border-purple-200' },
];

// Legacy statuses that may exist in old rows — map them back into the pipeline.
const LEGACY_STATUS_MAP = {
  pending: 'interested',
  in_selection: 'submitted',
  selected: 'awarded',
  not_selected: 'submitted',
  cancelled: 'interested',
};
const normalizeStatus = (s) => LEGACY_STATUS_MAP[s] || s || 'interested';

const PHASES = ['Phase 1', 'Phase 2', 'Phase 2a', 'Phase 2b', 'Phase 3', 'Phase 4', 'Unknown'];

const STUDY_TYPES = [
  { value: 'pre_feasibility', label: 'Pre-Feasibility' },
  { value: 'feasibility', label: 'Feasibility' },
];

const emptyTrackerEntry = {
  study_type: 'feasibility',
  submission_date: '',
  sponsor: '',
  protocol_study_name: '',
  indication: '',
  phase: '',
  pi_name: '',
  site_location: '',
  enrollment_projection: '',
  rationale_key_strengths: '',
  status: 'interested',
  status_detail: '',
  status_change_date: '',
  study_stage: '',
  psv_date: '',
  psv_time: '',
  expected_patient_population: '',
  equipment_needed: '',
  follow_up_date: '',
  notes: ''
};

export default function Tracker() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [formData, setFormData] = useState(emptyTrackerEntry);
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortField, setSortField] = useState('submission_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const loadEntries = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tracker`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      toast.error('Failed to load tracker data');
    } finally {
      setLoading(false);
    }
    // Module-level constant + stable setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData(entry);
    } else {
      setEditingEntry(null);
      setFormData(emptyTrackerEntry);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setFormData(emptyTrackerEntry);
  };

  const handleSubmit = async () => {
    if (!formData.sponsor || !formData.protocol_study_name) {
      toast.error('Sponsor and Protocol/Study Name are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingEntry 
        ? `${API_URL}/api/tracker/${editingEntry.id}`
        : `${API_URL}/api/tracker`;
      
      const response = await fetch(url, {
        method: editingEntry ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingEntry ? 'Entry updated!' : 'Entry added!');
        handleCloseDialog();
        loadEntries();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/tracker/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Entry deleted');
        loadEntries();
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleExport = () => {
    // Delegate CSV generation to the backend for consistent escaping.
    // Respect the currently-applied filters.
    const params = new URLSearchParams();
    if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
    if (filterType && filterType !== 'all') params.set('study_type', filterType);
    const qs = params.toString();
    const url = `${API_URL}/api/tracker/export${qs ? `?${qs}` : ''}`;
    // Trigger the browser download
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Exporting CSV...');
  };

  // Filtering and sorting
  const filteredEntries = useMemo(() => {
    let filtered = [...entries];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.sponsor?.toLowerCase().includes(query) ||
        e.protocol_study_name?.toLowerCase().includes(query) ||
        e.indication?.toLowerCase().includes(query) ||
        e.pi_name?.toLowerCase().includes(query) ||
        e.site_location?.toLowerCase().includes(query)
      );
    }
    
    // Status filter (pipeline-aware: maps legacy statuses)
    if (filterStatus !== 'all') {
      filtered = filtered.filter(e => normalizeStatus(e.status) === filterStatus);
    }
    
    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(e => e.study_type === filterType);
    }
    
    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField.includes('date')) {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  }, [entries, searchQuery, filterStatus, filterType, sortField, sortOrder]);

  const stats = useMemo(() => {
    const preFeas = entries.filter(e => e.study_type === 'pre_feasibility').length;
    const feas = entries.filter(e => e.study_type === 'feasibility').length;
    const count = (target) => entries.filter(e => normalizeStatus(e.status) === target).length;
    return {
      preFeas,
      feas,
      total: entries.length,
      interested: count('interested'),
      submitted: count('submitted'),
      awarded: count('awarded'),
      contracted: count('contracted'),
    };
  }, [entries]);

  const getStatusBadge = (status) => {
    const norm = normalizeStatus(status);
    const statusInfo = STATUSES.find(s => s.value === norm) || STATUSES[0];
    return <Badge className={`${statusInfo.color} border`}>{statusInfo.label}</Badge>;
  };

  const handleAdvanceStatus = async (entry, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/tracker/${entry.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, status_detail: entry.status_detail || '' }),
      });
      if (!response.ok) throw new Error('failed');
      toast.success(`Moved to ${newStatus}`);
      loadEntries();
    } catch {
      toast.error('Could not update status');
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']" data-testid="tracker-title">
            Feasibility Tracker
          </h1>
          <p className="text-slate-600 mt-1">Track all pre-feasibility and feasibility submissions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button className="btn-accent" onClick={() => handleOpenDialog()} data-testid="add-entry-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Pipeline Pills (Interested → Submitted → Awarded → Contracted) */}
      <Card className="stryde-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 overflow-x-auto">
            {[
              { value: 'all',         label: 'All',         count: stats.total,      tone: 'text-slate-700 ring-slate-300 bg-slate-50' },
              { value: 'interested',  label: 'Interested',  count: stats.interested, tone: 'text-amber-700 ring-amber-300 bg-amber-50' },
              { value: 'submitted',   label: 'Submitted',   count: stats.submitted,  tone: 'text-blue-700 ring-blue-300 bg-blue-50' },
              { value: 'awarded',     label: 'Awarded',     count: stats.awarded,    tone: 'text-emerald-700 ring-emerald-300 bg-emerald-50' },
              { value: 'contracted',  label: 'Contracted',  count: stats.contracted, tone: 'text-purple-700 ring-purple-300 bg-purple-50' },
            ].map((p, i, arr) => (
              <div key={p.value} className="flex items-center gap-2 flex-1 min-w-fit">
                <button
                  type="button"
                  onClick={() => setFilterStatus(p.value)}
                  data-testid={`pipeline-${p.value}`}
                  className={`flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-full ring-1 transition
                    ${p.tone}
                    ${filterStatus === p.value ? 'ring-2 ring-offset-2 ring-offset-white shadow-sm font-semibold' : 'hover:ring-2'}
                  `}
                >
                  <span className="text-sm">{p.label}</span>
                  <span className="text-sm font-mono bg-white/70 px-2 py-0.5 rounded-full">{p.count}</span>
                </button>
                {i < arr.length - 1 && <span className="text-slate-300">›</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="stryde-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search sponsor, protocol, indication, PI..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 focus-visible:ring-[#2D7DD2]"
                data-testid="search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-40" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {STUDY_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="stryde-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('submission_date')}>
                  <div className="flex items-center gap-1">Submitted<ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Site</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('sponsor')}>
                  <div className="flex items-center gap-1">Sponsor<ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Protocol</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Indication</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Phase</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">PI</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status Detail</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100" onClick={() => toggleSort('status_change_date')}>
                  <div className="flex items-center gap-1">Status Change<ArrowUpDown className="w-3 h-3" /></div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Study Stage</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">PSV</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Expected Pop.</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Equipment</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`skeleton-row-${i}`}>
                    {[...Array(16)].map((_, j) => (
                      <td key={`skeleton-cell-${i}-${j}`} className="px-3 py-3">
                        <Skeleton className="h-5 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={16} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FileText className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500">No entries found</p>
                      <Button 
                        variant="link" 
                        className="mt-2 text-[#2D7DD2]"
                        onClick={() => handleOpenDialog()}
                      >
                        Add your first entry
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => {
                  // "Site" auto-derives from PI name + their location when missing.
                  const site = entry.site_location || entry.pi_name || '-';
                  const psv = entry.psv_date
                    ? `${entry.psv_date}${entry.psv_time ? ' ' + entry.psv_time : ''}`
                    : '-';
                  return (
                  <tr key={entry.id} className="hover:bg-slate-50 transition-colors" data-testid={`tracker-row-${entry.id}`}>
                    <td className="px-3 py-3">
                      <Badge className={entry.study_type === 'pre_feasibility' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                        {entry.study_type === 'pre_feasibility' ? 'Pre-Feas' : 'Feas'}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {entry.submission_date || '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-700 max-w-[160px] truncate" title={site}>
                      {site}
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-medium text-slate-900">{entry.sponsor}</span>
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[180px] truncate" title={entry.protocol_study_name}>
                      {entry.protocol_study_name}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[140px] truncate" title={entry.indication}>
                      {entry.indication || '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600">{entry.phase || '-'}</td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[140px] truncate" title={entry.pi_name}>
                      {entry.pi_name || '-'}
                    </td>
                    <td className="px-3 py-3">{getStatusBadge(entry.status)}</td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[140px] truncate" title={entry.status_detail}>
                      {entry.status_detail || '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{entry.status_change_date || '-'}</td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[140px] truncate" title={entry.study_stage}>
                      {entry.study_stage || '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{psv}</td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[160px] truncate" title={entry.expected_patient_population}>
                      {entry.expected_patient_population || '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 max-w-[160px] truncate" title={entry.equipment_needed}>
                      {entry.equipment_needed || '-'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewEntry(entry)} data-testid={`view-${entry.id}`}>
                          <Eye className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(entry)} data-testid={`edit-${entry.id}`}>
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Select value="" onValueChange={(v) => handleAdvanceStatus(entry, v)}>
                          <SelectTrigger className="h-8 w-[42px] px-2 text-xs" data-testid={`advance-${entry.id}`}>
                            <span className="text-slate-500">⇨</span>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map(s => (
                              <SelectItem key={s.value} value={s.value}>Move to {s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Entry</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this tracker entry? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleDelete(entry.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>
        {filteredEntries.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-sm text-slate-500">
              Showing {filteredEntries.length} of {entries.length} entries
            </p>
          </div>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add New Entry'}</DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the feasibility tracker entry' : 'Add a new pre-feasibility or feasibility entry'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Study Type *</Label>
              <Select 
                value={formData.study_type} 
                onValueChange={(v) => setFormData(p => ({ ...p, study_type: v }))}
              >
                <SelectTrigger data-testid="form-study-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STUDY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Submission Date</Label>
              <Input
                type="text"
                placeholder="e.g., Nov 2025 or 2025-11-15"
                value={formData.submission_date}
                onChange={(e) => setFormData(p => ({ ...p, submission_date: e.target.value }))}
                data-testid="form-submission-date"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Sponsor *</Label>
              <Input
                placeholder="e.g., Pfizer, Novartis"
                value={formData.sponsor}
                onChange={(e) => setFormData(p => ({ ...p, sponsor: e.target.value }))}
                data-testid="form-sponsor"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Protocol/Study Name *</Label>
              <Input
                placeholder="e.g., Phase 3 Diabetes Study"
                value={formData.protocol_study_name}
                onChange={(e) => setFormData(p => ({ ...p, protocol_study_name: e.target.value }))}
                data-testid="form-protocol"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Indication</Label>
              <Input
                placeholder="e.g., Type 2 Diabetes"
                value={formData.indication}
                onChange={(e) => setFormData(p => ({ ...p, indication: e.target.value }))}
                data-testid="form-indication"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Phase</Label>
              <Select 
                value={formData.phase} 
                onValueChange={(v) => setFormData(p => ({ ...p, phase: v }))}
              >
                <SelectTrigger data-testid="form-phase">
                  <SelectValue placeholder="Select phase" />
                </SelectTrigger>
                <SelectContent>
                  {PHASES.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>PI Name</Label>
              <Input
                placeholder="e.g., Dr. John Smith, MD"
                value={formData.pi_name}
                onChange={(e) => setFormData(p => ({ ...p, pi_name: e.target.value }))}
                data-testid="form-pi-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Site Location</Label>
              <Input
                placeholder="e.g., Stryde Cardiology - Dallas, TX"
                value={formData.site_location}
                onChange={(e) => setFormData(p => ({ ...p, site_location: e.target.value }))}
                data-testid="form-site-location"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Enrollment Projection</Label>
              <Input
                placeholder="e.g., 10-15 subjects"
                value={formData.enrollment_projection}
                onChange={(e) => setFormData(p => ({ ...p, enrollment_projection: e.target.value }))}
                data-testid="form-enrollment"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}
              >
                <SelectTrigger data-testid="form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Status Detail</Label>
              <Input
                placeholder='e.g., "Site List", "Awaiting Response"'
                value={formData.status_detail}
                onChange={(e) => setFormData(p => ({ ...p, status_detail: e.target.value }))}
                data-testid="form-status-detail"
              />
            </div>

            <div className="space-y-2">
              <Label>Status Change Date</Label>
              <Input
                type="date"
                value={formData.status_change_date}
                onChange={(e) => setFormData(p => ({ ...p, status_change_date: e.target.value }))}
                data-testid="form-status-change-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Study Stage</Label>
              <Input
                placeholder="e.g., Evaluating Sites"
                value={formData.study_stage}
                onChange={(e) => setFormData(p => ({ ...p, study_stage: e.target.value }))}
                data-testid="form-study-stage"
              />
            </div>

            <div className="space-y-2">
              <Label>Follow-Up Date</Label>
              <Input
                type="text"
                placeholder="e.g., Jan 15, 2026"
                value={formData.follow_up_date}
                onChange={(e) => setFormData(p => ({ ...p, follow_up_date: e.target.value }))}
                data-testid="form-followup-date"
              />
            </div>

            <div className="space-y-2">
              <Label>PSV Date</Label>
              <Input
                type="date"
                value={formData.psv_date}
                onChange={(e) => setFormData(p => ({ ...p, psv_date: e.target.value }))}
                data-testid="form-psv-date"
              />
            </div>

            <div className="space-y-2">
              <Label>PSV Time</Label>
              <Input
                type="time"
                value={formData.psv_time}
                onChange={(e) => setFormData(p => ({ ...p, psv_time: e.target.value }))}
                data-testid="form-psv-time"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Expected Patient Population</Label>
              <Textarea
                placeholder="e.g., 60-80 male AGA patients (Norwood IV-V) ages 18-55..."
                value={formData.expected_patient_population}
                onChange={(e) => setFormData(p => ({ ...p, expected_patient_population: e.target.value }))}
                className="min-h-[60px]"
                data-testid="form-expected-pop"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Equipment Needed</Label>
              <Textarea
                placeholder="e.g., -80°C freezer, IATA-certified courier, ECG, CCTA capability..."
                value={formData.equipment_needed}
                onChange={(e) => setFormData(p => ({ ...p, equipment_needed: e.target.value }))}
                className="min-h-[60px]"
                data-testid="form-equipment"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Rationale/Key Strengths</Label>
              <Textarea
                placeholder="Site capabilities, patient population, PI experience..."
                value={formData.rationale_key_strengths}
                onChange={(e) => setFormData(p => ({ ...p, rationale_key_strengths: e.target.value }))}
                className="min-h-[80px]"
                data-testid="form-rationale"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                className="min-h-[60px]"
                data-testid="form-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="btn-primary"
              data-testid="form-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {editingEntry ? 'Update' : 'Add'} Entry
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewEntry} onOpenChange={() => setViewEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewEntry?.protocol_study_name}
              <Badge className={viewEntry?.study_type === 'pre_feasibility' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}>
                {viewEntry?.study_type === 'pre_feasibility' ? 'Pre-Feasibility' : 'Feasibility'}
              </Badge>
            </DialogTitle>
            <DialogDescription>{viewEntry?.sponsor}</DialogDescription>
          </DialogHeader>
          
          {viewEntry && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Submission Date</p>
                <p className="font-medium">{viewEntry.submission_date || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Status</p>
                {getStatusBadge(viewEntry.status)}
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Indication</p>
                <p className="font-medium">{viewEntry.indication || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Phase</p>
                <p className="font-medium">{viewEntry.phase || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">PI Name</p>
                <p className="font-medium">{viewEntry.pi_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Site Location</p>
                <p className="font-medium">{viewEntry.site_location || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Enrollment Projection</p>
                <p className="font-medium">{viewEntry.enrollment_projection || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Follow-Up Date</p>
                <p className="font-medium">{viewEntry.follow_up_date || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 uppercase">Rationale/Key Strengths</p>
                <p className="font-medium whitespace-pre-wrap">{viewEntry.rationale_key_strengths || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 uppercase">Notes</p>
                <p className="font-medium whitespace-pre-wrap">{viewEntry.notes || '-'}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewEntry(null)}>Close</Button>
            <Button 
              onClick={() => {
                handleOpenDialog(viewEntry);
                setViewEntry(null);
              }}
              className="btn-primary"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
